/**
 * Frontend Analytics (PostHog)
 *
 * Provides PostHog tracking helpers for the frontend.
 * Uses cookieless mode (persistence: 'memory') to avoid cookie consent banners.
 *
 * PRIVACY: Never track prompt content, response content, or API keys.
 */

import posthog from 'posthog-js'

// ============================================================================
// UTM Parameter Types & Storage (S1.17.3 - CAC Tracking)
// ============================================================================

/** UTM parameters captured from the URL on page load */
export interface UtmParams {
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
}

/**
 * In-memory cache of UTM params captured at page load.
 * Stored in memory (not localStorage) to match our cookieless approach.
 * Persisted via sessionStorage so they survive SPA navigation but not new sessions.
 */
let cachedUtmParams: UtmParams | null = null

const UTM_STORAGE_KEY = 'arbinex_utm_params'

/**
 * Capture UTM parameters from the current URL.
 * Should be called once at app startup (in initPostHog or main.tsx).
 * Uses sessionStorage to persist across SPA navigation within the same session.
 */
export function captureUtmParams(): UtmParams {
  // If already captured this session (in-memory), return cached
  if (cachedUtmParams) return cachedUtmParams

  // Check URL for UTM params
  const urlParams = new URLSearchParams(globalThis.location.search)
  const fromUrl: UtmParams = {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
  }

  // If URL has UTM params, store them in sessionStorage and cache
  const hasUtmInUrl = fromUrl.utm_source || fromUrl.utm_medium || fromUrl.utm_campaign
  if (hasUtmInUrl) {
    cachedUtmParams = fromUrl
    try {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(fromUrl))
    } catch {
      // sessionStorage may be unavailable (private browsing, etc.)
    }
    return fromUrl
  }

  // Fall back to sessionStorage (e.g., user navigated away from landing page)
  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY)
    if (stored) {
      cachedUtmParams = JSON.parse(stored) as UtmParams
      return cachedUtmParams
    }
  } catch {
    // sessionStorage may be unavailable
  }

  // No UTM params found
  cachedUtmParams = { utm_source: null, utm_medium: null, utm_campaign: null }
  return cachedUtmParams
}

/**
 * Get previously captured UTM parameters.
 * Returns null values if no UTM params were found.
 */
export function getUtmParams(): UtmParams {
  if (cachedUtmParams) return cachedUtmParams
  return captureUtmParams()
}

/**
 * Reset cached UTM params (for testing)
 */
export function _resetUtmParamsForTesting(): void {
  cachedUtmParams = null
  try {
    sessionStorage.removeItem(UTM_STORAGE_KEY)
  } catch {
    // ignore
  }
}

// ============================================================================
// PostHog Initialization
// ============================================================================

/**
 * Initialize PostHog on the frontend
 * Called once in main.tsx
 */
export function initPostHog(): void {
  // Always capture UTM params on page load, even if PostHog is disabled
  captureUtmParams()

  const apiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY

  if (!apiKey) return

  // Skip analytics in development to avoid polluting production data
  if (import.meta.env.DEV) return

  // Skip analytics outside production. Staging shares this PostHog project
  // with production (single-project free plan), so staging traffic must
  // never reach it. Fail-closed on purpose: only the literal 'production'
  // enables tracking, so a missing/misconfigured/renamed VITE_ENV disables
  // it rather than silently falling through.
  if (import.meta.env.VITE_ENV !== 'production') return

  // Route through our control-plane reverse proxy to bypass ad blockers.
  // Falls back to direct PostHog URL if VITE_API_URL is not set.
  const apiUrl = import.meta.env.VITE_API_URL
  const host = apiUrl ? `${apiUrl}/a` : (import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com')

  posthog.init(apiKey, {
    api_host: host,
    // Cookieless mode: no cookies, no consent banner needed
    persistence: 'memory',
    // Autocapture clicks, pageviews, etc.
    autocapture: true,
    // Capture pageview on init
    capture_pageview: true,
    // Capture pageleave for session duration
    capture_pageleave: true,
    // Disable session recording by default (enable later in Phase 3)
    disable_session_recording: true,
  })
}

/**
 * Identify user after login/signup (links anonymous events to user).
 * Uses $set_once for UTM attribution so first-touch values are preserved.
 */
export function identifyUser(
  userId: string,
  properties: Record<string, string | boolean | null>,
  setOnceProperties?: Record<string, string | boolean | null>
): void {
  if (!posthog.__loaded) return
  posthog.identify(userId, properties, setOnceProperties)
}

/**
 * Reset user identity on logout
 */
export function resetUser(): void {
  if (!posthog.__loaded) return
  posthog.reset()
}

/**
 * Track a custom event
 */
export function trackEvent(event: string, properties?: Record<string, string | number | boolean | null>): void {
  if (!posthog.__loaded) return
  posthog.capture(event, properties)
}

// ============================================================================
// Trial Analytics Events (E1.12) — REMOVED 2026-08-24 with the paid trial (§2)
// ============================================================================
//
// `trackTrialStarted`, `trackTrialBannerDismissed` and `trackTrialUpgradeClicked`
// are gone. The first two lost their only callers when the trial checkout and
// the Pro-trial countdown banner were deleted; the third already had none.
//
// The PostHog events `trial_started`, `trial_banner_dismissed` and
// `trial_upgrade_clicked` therefore stop arriving. That is accurate rather than
// a regression — nothing can start a Stripe trial any more — but any dashboard
// or funnel built on them will read as flat, so it is stated here rather than
// discovered from a chart.
//
// The FREE-tier evaluation trial has no analytics events of its own and did not
// use these.
//
// FOUR functions, not three — `trackTrialCancelClicked` went with them. It was
// found by a review pass after the first three were removed, which is the point:
// an enumeration that stops at the reported list reads as closed when it is not.
// The events that stop arriving are therefore `trial_started`,
// `trial_banner_dismissed`, `trial_upgrade_clicked` AND `trial_cancel_clicked`.
//
// ⚠️ THREE PostHog PERSON PROPERTIES stop being written too, and those behave
// differently from events. `tierStore.loadTrialInfo` was the only
// `posthog.people.set` call for `has_used_trial`, `is_trialing` and
// `trial_days_remaining`, and it went with the paid trial. Person properties are
// STICKY: a profile already carrying `is_trialing: true` keeps it forever, so a
// "currently trialing" cohort will never empty on its own. The blast radius is
// near-zero here — production held zero Stripe trials when this shipped — but
// any such cohort has to be cleared in PostHog, not in this codebase.

// ============================================================================
// Response Feedback Events (S1.11.2 - deferred from initial analytics setup)
// ============================================================================

export function trackResponseCopied(modelId: string, feedbackType: 'selection' | 'synthesis'): void {
  trackEvent('response_copied', { model_id: modelId, feedback_type: feedbackType })
}

export function trackResponseRated(rating: 'up' | 'down', modelId: string, feedbackType: 'selection' | 'synthesis'): void {
  trackEvent('response_rated', { rating, model_id: modelId, feedback_type: feedbackType })
}
